import React, { FC } from 'react'
import { Alert, AlertIcon, Button, Checkbox, Heading, HStack, Stack, Text } from '@chakra-ui/react'
import { CashoutFinalizationValidation } from '@homevest/types/cashouts'

interface CashoutFinalizationChecksProps {
  validation: CashoutFinalizationValidation
  canOverride: boolean
  acknowledged: boolean
  onAcknowledgedChange: (acknowledged: boolean) => void
  onRecheck: () => void
  isRechecking: boolean
  onReviewAcceleratedRent: () => void
  acceleratedRentActionLabel: string
}

export const canFinalizeWithCashoutValidation = (
  validation: CashoutFinalizationValidation,
  canOverride: boolean,
  acknowledgedValidationFingerprint: string | null
): boolean =>
  validation.flags.length === 0 ||
  (canOverride && acknowledgedValidationFingerprint === validation.fingerprint)

export const getCashoutValidationSubmission = (
  validation: CashoutFinalizationValidation,
  acknowledgedValidationFingerprint: string | null
):
  | { skip_validation: 0 }
  | { skip_validation: 1; acknowledged_validation_fingerprint: string }
  | null => {
  if (validation.flags.length === 0) {
    return { skip_validation: 0 }
  }
  if (acknowledgedValidationFingerprint !== validation.fingerprint) {
    return null
  }
  return {
    skip_validation: 1,
    acknowledged_validation_fingerprint: validation.fingerprint
  }
}

export const CashoutFinalizationChecks: FC<CashoutFinalizationChecksProps> = ({
  validation,
  canOverride,
  acknowledged,
  onAcknowledgedChange,
  onRecheck,
  isRechecking,
  onReviewAcceleratedRent,
  acceleratedRentActionLabel
}) => {
  const warnings = validation.warnings ?? []
  const hasFlags = validation.flags.length > 0

  if (!hasFlags && warnings.length === 0) {
    return (
      <Stack
        as='section'
        spacing={2}
        aria-labelledby='cashout-checks-heading'
        borderWidth='1px'
        borderColor='gray.200'
        borderRadius='md'
        bg='white'
        p={4}
      >
        <Heading as='h3' size='sm' id='cashout-checks-heading'>
          Cashout checks
        </Heading>
        <Alert status='success' borderRadius='md' py={2}>
          <AlertIcon />
          <Text fontWeight='semibold'>All cashout checks passed</Text>
        </Alert>
      </Stack>
    )
  }

  return (
    <Stack
      as='section'
      spacing={3}
      aria-labelledby='cashout-checks-heading'
      borderWidth='1px'
      borderColor='gray.200'
      borderRadius='md'
      bg='white'
      p={4}
    >
      <HStack justify='space-between' flexWrap='wrap' gap={2}>
        <Heading as='h3' size='sm' id='cashout-checks-heading'>
          Cashout checks
        </Heading>
        <Button size='xs' variant='outline' onClick={onRecheck} isLoading={isRechecking}>
          Recheck cashout checks
        </Button>
      </HStack>
      {warnings.map((warning) => (
        <Alert key={warning.code} status='info' borderRadius='md' alignItems='start'>
          <AlertIcon mt={1} />
          <Stack spacing={1}>
            <Text fontWeight='semibold'>{warning.title}</Text>
            <Text fontSize='sm'>{warning.message}</Text>
          </Stack>
        </Alert>
      ))}
      {hasFlags && (
        <Stack spacing={3} role='group' aria-label='Checks requiring attention'>
          {validation.flags.map((flag) => (
            <Alert key={flag.code} status='warning' borderRadius='md' alignItems='start'>
              <AlertIcon mt={1} />
              <Stack
                flex='1'
                direction={{ base: 'column', md: 'row' }}
                align={{ base: 'start', md: 'center' }}
                justify='space-between'
                spacing={3}
              >
                <Stack spacing={1}>
                  <Text fontWeight='semibold'>{flag.title}</Text>
                  <Text fontSize='sm'>{flag.message}</Text>
                </Stack>
                {flag.code === 'ACCELERATED_RENT_NEEDS_REVIEW' && (
                  <Button
                    size='sm'
                    flexShrink={0}
                    colorScheme='orange'
                    variant='outline'
                    onClick={onReviewAcceleratedRent}
                  >
                    {acceleratedRentActionLabel}
                  </Button>
                )}
              </Stack>
            </Alert>
          ))}
          {canOverride ? (
            <Checkbox
              isChecked={acknowledged}
              onChange={(event) => onAcknowledgedChange(event.target.checked)}
            >
              I understand these checks have not passed and want to finalize this cashout anyway.
            </Checkbox>
          ) : (
            <Text fontSize='sm' color='gray.600'>
              Resolve these checks or ask a cashout admin to override them before finalizing.
            </Text>
          )}
        </Stack>
      )}
    </Stack>
  )
}
